import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input, Label } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { ROLE_LABELS } from "../lib/utils";
import { Plus, Trash2 } from "lucide-react";
import { SecurityMfa, MfaPolicyCard } from "../components/SecurityMfa";
import { toast } from "sonner";
import { EsgTaxonomyManager } from "../components/EsgTaxonomyManager";
import { ImportTemplatesManager } from "../components/ImportTemplatesManager";
import { FactorVerification } from "../components/FactorVerification";
import { FacilitiesManager } from "../components/FacilitiesManager";
import { GroupMaster } from "../components/GroupMaster";

const ROLES = ["esg_manager", "data_officer", "approver", "auditor", "board"];
const SECTORS = ["Consumer Goods", "Extractives & Minerals Processing", "Financials", "Food & Beverage", "Health Care", "Infrastructure", "Renewable Resources & Alternative Energy", "Resource Transformation", "Services", "Technology & Communications", "Transportation"];
const ENTITY_TYPES = ["Private Limited (Pte Ltd)", "Public Listed (Ltd)", "Limited Liability Partnership", "Partnership", "Sole Proprietorship", "Branch / Subsidiary", "Small/Medium Enterprise (SME)"];
const REG_TIERS = ["SGX-Listed — Mandatory (Phase 1)", "Large Non-Listed Company — Mandatory (Phase 2, FY2027)", "SME / Non-Mandatory — Voluntary", "Not yet assessed"];
const LISTING_STATUS = ["SGX Mainboard", "SGX Catalist", "Other exchange", "Not listed"];
const FRAMEWORK_OPTIONS = ["SGX Rule 711A", "SGX Rule 711B", "IFRS S1", "IFRS S2", "GRI", "SASB", "TCFD", "SBTi", "CDP", "GHG Protocol", "Carbon Pricing Act", "Energy Conservation Act", "ISO 14001", "ISO 45001", "ISO 50001", "EU CBAM", "UN SDG"];
const COMMON_OBLIGATIONS = ["Annual sustainability report", "Climate-related disclosures (ISSB-aligned)", "Board statement on sustainability", "SGX Core ESG Metrics", "Carbon tax reporting (CPA)", "NEA energy use reporting (ECA)", "Scope 1 & 2 GHG inventory", "Scope 3 screening"];
const LEASED_BOUNDARY_OPTS = [
  { v: "review", l: "Flag each line for review (default)" },
  { v: "scope2", l: "Operational control → report as Scope 2" },
  { v: "scope3_cat8", l: "Not operated by us → Scope 3 Cat 8 (upstream leased assets)" },
];
const LEASED_BOUNDARY_LABEL = { review: "Flag for review", scope2: "Operational control → Scope 2", scope3_cat8: "Financial control → Scope 3 Cat 8" };
const canEditTenant = (role) => ["tenant_admin", "esg_manager", "super_admin"].includes(role);

export default function Settings() {
  const { user, tenant, refresh } = useAuth();
  const [users, setUsers] = useState([]);
  const [openUser, setOpenUser] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", name: "", password: "", role: "data_officer" });
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [customObl, setCustomObl] = useState("");
  const [boundaryPreview, setBoundaryPreview] = useState(null);
  const [boundaryApplying, setBoundaryApplying] = useState(false);
  const canEdit = canEditTenant(user.role);
  const applyBoundary = async () => {
    const pol = form.leased_asset_boundary || "review";
    const n = (boundaryPreview?.to_scope2 || 0) + (boundaryPreview?.to_scope3_cat8 || 0);
    if (!window.confirm(`Reclassify ${n} committed leased-energy record(s) under "${LEASED_BOUNDARY_LABEL[pol]}"? This updates existing records.`)) return;
    setBoundaryApplying(true);
    try {
      const { data } = await api.post("/esg-import/boundary-apply", null, { params: { policy: pol } });
      toast.success(`Reclassified ${data.applied} record(s) — ${data.to_scope2} Scope 2, ${data.to_scope3_cat8} Scope 3 Cat 8`);
      const r = await api.get("/esg-import/boundary-preview", { params: { policy: pol } });
      setBoundaryPreview(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to apply boundary"); } finally { setBoundaryApplying(false); }
  };

  const loadUsers = async () => {
    const { data } = await api.get("/users");
    setUsers(data.users);
  };
  useEffect(() => { loadUsers(); }, []);
  useEffect(() => { if (tenant) setForm(tenant); }, [tenant]);
  useEffect(() => {
    if (!editing) { setBoundaryPreview(null); return; }
    const pol = form.leased_asset_boundary || "review";
    let cancel = false;
    const t = setTimeout(() => {
      api.get("/esg-import/boundary-preview", { params: { policy: pol } })
        .then((r) => { if (!cancel) setBoundaryPreview(r.data); })
        .catch(() => { if (!cancel) setBoundaryPreview(null); });
    }, 300);
    return () => { cancel = true; clearTimeout(t); };
  }, [editing, form.leased_asset_boundary]);

  const startEdit = () => { setForm(tenant || {}); setEditing(true); };
  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggleList = (k, val) => setForm((p) => {
    const arr = p[k] || [];
    return { ...p, [k]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] };
  });
  const saveTenant = async () => {
    try {
      await api.patch("/tenants/me", {
        name: form.name, registration_number: form.registration_number, legal_entity_type: form.legal_entity_type,
        sector: form.sector, employees: form.employees ? Number(form.employees) : undefined,
        revenue_sgd: form.revenue_sgd ? Number(form.revenue_sgd) : undefined,
        total_assets_sgd: form.total_assets_sgd ? Number(form.total_assets_sgd) : undefined,
        fy_end_month: form.fy_end_month ? Number(form.fy_end_month) : undefined,
        currency: form.currency, primary_contact: form.primary_contact,
        regulatory_tier: form.regulatory_tier, listing_status: form.listing_status,
        cpa_taxable: form.cpa_taxable, eca_registered: form.eca_registered,
        obligations: form.obligations, frameworks_selected: form.frameworks_selected,
        leased_asset_boundary: form.leased_asset_boundary || "review",
      });
      toast.success("Profile updated");
      setEditing(false);
      refresh && refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
  };

  const create = async () => {
    try {
      await api.post("/users", newUser);
      toast.success("User created");
      setOpenUser(false);
      setNewUser({ email: "", name: "", password: "", role: "data_officer" });
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete user?")) return;
    await api.delete(`/users/${id}`);
    loadUsers();
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div>
        <div className="label-eyebrow mb-1">Settings</div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Tenant & access</h1>
      </div>

      <Tabs defaultValue="profile">
        <TabsList>
          <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">Users & Roles</TabsTrigger>
          <TabsTrigger value="obligations" data-testid="tab-obligations">Obligations</TabsTrigger>
          <TabsTrigger value="taxonomy" data-testid="tab-taxonomy">ESG Taxonomy</TabsTrigger>
          <TabsTrigger value="groups" data-testid="tab-groups">Group Master</TabsTrigger>
          <TabsTrigger value="facilities" data-testid="tab-facilities">Facilities</TabsTrigger>
          <TabsTrigger value="factors" data-testid="tab-factors">Factor Sign-off</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Import Templates</TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security">Security</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="space-y-4">
          <div className="card-flat p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-semibold text-lg">Entity profile</h2>
              {canEdit && !editing && <Button variant="outline" size="sm" onClick={startEdit} data-testid="edit-profile-btn">Edit profile</Button>}
              {editing && <div className="flex gap-2"><Button variant="ghost" size="sm" onClick={() => setEditing(false)} data-testid="cancel-profile-btn">Cancel</Button><Button variant="primary" size="sm" onClick={saveTenant} data-testid="save-profile-btn">Save</Button></div>}
            </div>
            {!editing ? (
              <div className="grid grid-cols-2 gap-4 text-sm">
                <Field label="Company" v={tenant?.name} />
                <Field label="Registration / UEN" v={tenant?.registration_number} />
                <Field label="Legal entity" v={tenant?.legal_entity_type} />
                <Field label="Industry (SASB)" v={`${tenant?.industry_code || ""} — ${tenant?.industry_name || ""}`} />
                <Field label="Sector (materiality & financing)" v={tenant?.sector} />
                <Field label="Primary contact" v={tenant?.primary_contact} />
                <Field label="Revenue (SGD)" v={tenant?.revenue_sgd ? `S$ ${tenant.revenue_sgd.toLocaleString()}` : "—"} />
                <Field label="Total assets (SGD)" v={tenant?.total_assets_sgd ? `S$ ${tenant.total_assets_sgd.toLocaleString()}` : "—"} />
                <Field label="Employees" v={tenant?.employees} />
                <Field label="Currency" v={tenant?.currency} />
                <Field label="FY end" v={tenant?.fy_end_month ? `Month ${tenant.fy_end_month}` : "—"} />
                <Field label="Regulatory tier" v={tenant?.regulatory_tier} />
                <div className="col-span-2 text-[11px] text-muted-foreground border-t border-border pt-3 mt-1" data-testid="industry-sector-explainer">
                  <span className="font-medium text-foreground">Industry (SASB)</span> and <span className="font-medium text-foreground">Sector</span> are two distinct classification fields: the SASB industry drives report &amp; framework mapping (Control Tower, Sustainability Report), while the Sector drives Materiality Engine and Green Financing matching.
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Company</Label><Input value={form.name || ""} onChange={(e) => setF("name", e.target.value)} data-testid="profile-name" /></div>
                <div><Label>Registration / UEN</Label><Input value={form.registration_number || ""} onChange={(e) => setF("registration_number", e.target.value)} data-testid="profile-uen" /></div>
                <div><Label>Legal entity type</Label>
                  <Select value={form.legal_entity_type || ""} onValueChange={(v) => setF("legal_entity_type", v)}>
                    <SelectTrigger data-testid="profile-entity"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>{ENTITY_TYPES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label>Sector (materiality &amp; financing)</Label>
                  <Select value={form.sector || ""} onValueChange={(v) => setF("sector", v)}>
                    <SelectTrigger data-testid="profile-sector"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>{(form.sector && !SECTORS.includes(form.sector) ? [form.sector, ...SECTORS] : SECTORS).map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                  </Select>
                  <p className="text-[10px] text-muted-foreground mt-1">Distinct from your SASB industry ({form.industry_code || "—"}) — this field drives Materiality &amp; Green Financing.</p>
                </div>
                <div><Label>Primary contact</Label><Input value={form.primary_contact || ""} onChange={(e) => setF("primary_contact", e.target.value)} data-testid="profile-contact" /></div>
                <div><Label>Currency</Label><Input value={form.currency || ""} onChange={(e) => setF("currency", e.target.value)} placeholder="SGD" /></div>
                <div><Label>Revenue (SGD)</Label><Input type="number" value={form.revenue_sgd || ""} onChange={(e) => setF("revenue_sgd", e.target.value)} data-testid="profile-revenue" /></div>
                <div><Label>Total assets (SGD)</Label><Input type="number" value={form.total_assets_sgd || ""} onChange={(e) => setF("total_assets_sgd", e.target.value)} /></div>
                <div><Label>Employees</Label><Input type="number" value={form.employees || ""} onChange={(e) => setF("employees", e.target.value)} data-testid="profile-employees" /></div>
                <div><Label>FY end month</Label>
                  <Select value={String(form.fy_end_month || "")} onValueChange={(v) => setF("fy_end_month", v)}>
                    <SelectTrigger><SelectValue placeholder="Month" /></SelectTrigger>
                    <SelectContent>{Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <SelectItem key={m} value={String(m)}>Month {m}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
            )}
          </div>
          <ReportingLocaleCard />
        </TabsContent>

        <TabsContent value="users">
          <div className="card-flat">
            <div className="flex items-center justify-between p-4 border-b border-border bg-surface">
              <h2 className="font-display font-semibold text-lg">Team members</h2>
              <Dialog open={openUser} onOpenChange={setOpenUser}>
                <DialogTrigger asChild>
                  <Button variant="primary" size="sm" data-testid="add-user-btn"><Plus className="h-3 w-3" /> Add user</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Invite user</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label>Name</Label><Input value={newUser.name} onChange={(e) => setNewUser({...newUser, name: e.target.value})} data-testid="user-name-input" /></div>
                    <div><Label>Email</Label><Input type="email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} data-testid="user-email-input" /></div>
                    <div><Label>Temp password</Label><Input type="password" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} data-testid="user-password-input" /></div>
                    <div>
                      <Label>Role</Label>
                      <Select value={newUser.role} onValueChange={(v) => setNewUser({...newUser, role: v})}>
                        <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button variant="primary" onClick={create} data-testid="create-user-btn">Create</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid="user-row">
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell className="font-mono text-xs">{u.email}</TableCell>
                    <TableCell><Badge variant="outline">{ROLE_LABELS[u.role] || u.role}</Badge></TableCell>
                    <TableCell>
                      {u.id !== (typeof user.id === 'string' ? user.id : user._id) && (
                        <Button variant="ghost" size="icon" onClick={() => remove(u.id)} data-testid="delete-user-btn"><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="obligations">
          <div className="card-flat p-6">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-display font-semibold text-lg">Regulatory obligations</h2>
              {canEdit && !editing && <Button variant="outline" size="sm" onClick={startEdit} data-testid="edit-obligations-btn">Edit obligations</Button>}
              {editing && <div className="flex gap-2"><Button variant="ghost" size="sm" onClick={() => setEditing(false)}>Cancel</Button><Button variant="primary" size="sm" onClick={saveTenant} data-testid="save-obligations-btn">Save</Button></div>}
            </div>
            {!editing ? (
              <>
                <Badge variant="outline" className="mb-4">{tenant?.regulatory_tier}</Badge>
                <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                  <Field label="Listing status" v={tenant?.listing_status} />
                  <Field label="Carbon tax (CPA)" v={tenant?.cpa_taxable ? "Taxable (≥25k tCO₂e)" : "Below threshold"} />
                  <Field label="Energy Conservation Act" v={tenant?.eca_registered ? "Registered corporation" : "Not registered"} />
                  <Field label="Leased-asset energy boundary" v={LEASED_BOUNDARY_LABEL[tenant?.leased_asset_boundary || "review"]} />
                </div>
                <div className="label-eyebrow mb-2">Applicable obligations</div>
                <ul className="space-y-2 mb-5">
                  {(tenant?.obligations || []).map((o, i) => (
                    <li key={i} className="text-sm flex items-start gap-2 font-mono"><span className="text-accent mt-1">▸</span> {o}</li>
                  ))}
                  {!(tenant?.obligations || []).length && <li className="text-sm text-muted-foreground">None recorded.</li>}
                </ul>
                <div className="label-eyebrow mb-2">Selected frameworks & standards</div>
                <div className="flex flex-wrap gap-2">
                  {(tenant?.frameworks_selected || []).map((f) => <Badge key={f} variant="lime">{f}</Badge>)}
                  {!(tenant?.frameworks_selected || []).length && <span className="text-sm text-muted-foreground">None selected.</span>}
                </div>
              </>
            ) : (
              <div className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Regulatory tier</Label>
                    <Select value={form.regulatory_tier || ""} onValueChange={(v) => setF("regulatory_tier", v)}>
                      <SelectTrigger data-testid="obl-tier"><SelectValue placeholder="Select…" /></SelectTrigger>
                      <SelectContent>{(form.regulatory_tier && !REG_TIERS.includes(form.regulatory_tier) ? [form.regulatory_tier, ...REG_TIERS] : REG_TIERS).map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><Label>Listing status</Label>
                    <Select value={form.listing_status || ""} onValueChange={(v) => setF("listing_status", v)}>
                      <SelectTrigger data-testid="obl-listing"><SelectValue placeholder="Select…" /></SelectTrigger>
                      <SelectContent>{LISTING_STATUS.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                    </Select></div>
                </div>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={!!form.cpa_taxable} onChange={(e) => setF("cpa_taxable", e.target.checked)} className="accent-[hsl(var(--accent))]" data-testid="obl-cpa" /> Carbon-tax taxable (≥25k tCO₂e)</label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={!!form.eca_registered} onChange={(e) => setF("eca_registered", e.target.checked)} className="accent-[hsl(var(--accent))]" data-testid="obl-eca" /> ECA registered corporation</label>
                </div>
                <div className="max-w-xl">
                  <Label>Leased-asset energy boundary</Label>
                  <p className="text-[10px] text-muted-foreground mt-1 mb-1">How bulk imports classify landlord-supplied / leased / common-area energy. "Flag for review" holds each line for a human decision; the other options auto-apply the Scope boundary at import.</p>
                  <Select value={form.leased_asset_boundary || "review"} onValueChange={(v) => setF("leased_asset_boundary", v)}>
                    <SelectTrigger data-testid="obl-leased-boundary"><SelectValue /></SelectTrigger>
                    <SelectContent>{LEASED_BOUNDARY_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                  </Select>
                  {boundaryPreview && (
                    <div className="mt-2 card-flat p-2.5 text-[11px]" data-testid="boundary-preview">
                      {boundaryPreview.leased_total === 0 ? (
                        <span className="text-muted-foreground">No recent energy records look leased / landlord-supplied — this setting affects future imports.</span>
                      ) : (
                        <span><span className="font-semibold">{boundaryPreview.leased_total}</span> recent energy record(s) look leased → <span className="text-[#00E65B] font-medium" data-testid="boundary-preview-s2">{boundaryPreview.to_scope2} Scope 2</span>, <span className="text-sky-400 font-medium" data-testid="boundary-preview-s38">{boundaryPreview.to_scope3_cat8} Scope 3 Cat 8</span>, <span className="text-[#FFB800] font-medium" data-testid="boundary-preview-review">{boundaryPreview.to_review} flagged for review</span> under this setting.</span>
                      )}
                      {(boundaryPreview.to_scope2 + boundaryPreview.to_scope3_cat8) > 0 && canEdit && (
                        <div className="mt-2">
                          <Button variant="outline" size="sm" onClick={applyBoundary} disabled={boundaryApplying} data-testid="boundary-apply-btn">
                            {boundaryApplying ? "Applying…" : `Apply to ${boundaryPreview.to_scope2 + boundaryPreview.to_scope3_cat8} existing record(s)`}
                          </Button>
                          <span className="block text-[10px] text-muted-foreground mt-1">Reclassifies the committed records above now; the setting also applies to future imports once saved.</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <Label>Applicable obligations</Label>
                  <p className="text-[10px] text-muted-foreground mt-1 mb-1" data-testid="obl-helper">Your saved obligations are pre-selected below. Deselecting removes them on Save.</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {Array.from(new Set([...(form.obligations || []), ...COMMON_OBLIGATIONS])).map((o) => (
                      <button key={o} type="button" onClick={() => toggleList("obligations", o)} data-testid={`obl-item-${o.slice(0,8)}`}
                        className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${(form.obligations || []).includes(o) ? "bg-accent text-accent-foreground border-accent" : "border-border text-muted-foreground hover:border-accent/60"}`}>{o}</button>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-3 max-w-md">
                    <Input value={customObl} onChange={(e) => setCustomObl(e.target.value)} placeholder="Add a custom obligation…" className="h-8 text-xs" data-testid="obl-custom-input" />
                    <Button variant="outline" size="sm" className="h-8" data-testid="obl-custom-add-btn"
                      disabled={!customObl.trim() || (form.obligations || []).includes(customObl.trim())}
                      onClick={() => { toggleList("obligations", customObl.trim()); setCustomObl(""); }}>Add</Button>
                  </div>
                </div>
                <div>
                  <Label>Frameworks & standards</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {FRAMEWORK_OPTIONS.map((f) => (
                      <button key={f} type="button" onClick={() => toggleList("frameworks_selected", f)} data-testid={`fw-${f.replace(/[^a-zA-Z0-9]/g,"")}`}
                        className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${(form.frameworks_selected || []).includes(f) ? "bg-[#00E65B] text-black border-[#00E65B]" : "border-border text-muted-foreground hover:border-accent/60"}`}>{f}</button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="taxonomy" className="mt-4">
          <EsgTaxonomyManager canEdit={canEdit} />
        </TabsContent>

        <TabsContent value="groups" className="mt-4">
          <GroupMaster canEdit={canEdit} />
        </TabsContent>

        <TabsContent value="facilities" className="mt-4">
          <FacilitiesManager canEdit={canEdit} />
        </TabsContent>

        <TabsContent value="factors" className="mt-4">
          <FactorVerification canEdit={canEdit} />
        </TabsContent>

        <TabsContent value="security" className="mt-4 space-y-4"><SecurityMfa /><MfaPolicyCard /></TabsContent>

        <TabsContent value="templates" className="mt-4">
          <ImportTemplatesManager canEdit={canEdit} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Field({ label, v }) {
  return (
    <div>
      <div className="label-eyebrow mb-1">{label}</div>
      <div className="font-mono text-sm">{v || "—"}</div>
    </div>
  );
}

const RL_MEASURES = [["area", "Area"], ["energy", "Energy"], ["volume", "Volume"], ["mass", "Mass"], ["weight", "Weight"]];

function ReportingLocaleCard() {
  const [loc, setLoc] = useState(null);
  useEffect(() => { api.get("/locale/reporting").then((r) => setLoc(r.data)).catch(() => {}); }, []);
  if (!loc) return null;
  const labels = loc.unit_labels || {};
  return (
    <div className="card-flat p-6" data-testid="reporting-locale-card">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-display font-semibold text-lg">Reporting locale</h2>
        <Badge variant="lime" className="text-[10px]" data-testid="reporting-currency-badge">{loc.currency}</Badge>
      </div>
      <p className="text-xs text-muted-foreground mb-4 max-w-2xl">Consolidated dashboards, reports and exports display in your reporting locale — the customary units &amp; currency of your entity country ({loc.country}). Facility-local inputs are converted for reporting; every record keeps its original value, unit, currency and locked FX rate.</p>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <div><div className="label-eyebrow mb-1">Currency</div><div className="font-mono text-sm" data-testid="reporting-currency">{loc.currency}</div></div>
        {RL_MEASURES.map(([m, l]) => (
          <div key={m}><div className="label-eyebrow mb-1">{l}</div><div className="font-mono text-sm" data-testid={`reporting-unit-${m}`}>{labels[m] || loc.units?.[m]}</div></div>
        ))}
      </div>
      {!loc.resolved && <p className="text-[10px] text-[#FFB800] mt-2">No curated locale found for {loc.country} — using metric defaults. A super-admin can add it in the Country Locale Master.</p>}
    </div>
  );
}
